import { z } from 'zod';
import { decimalSchema } from './prisma-zod';
import { ModuleTypeEnum } from './module-type';
import { ContractorInvoiceEligibilityPolicyEnum } from './organization-settings';
import { UnbilledTimeSummarySchema } from './invoice';

export enum ContractorTypeEnum {
  INDIVIDUAL = 'INDIVIDUAL',
  SOLE_PROP = 'SOLE_PROP',
  LLC = 'LLC',
  CORPORATION = 'CORPORATION',
}

export enum ContractorInvoiceStatusEnum {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  PARTIALLY_TIME_APPROVED = 'PARTIALLY_TIME_APPROVED',
  TIME_APPROVED = 'TIME_APPROVED',
  PAYROLL_APPROVED = 'PAYROLL_APPROVED',
  PAID = 'PAID',
  ON_HOLD = 'ON_HOLD',
  RECALLED = 'RECALLED',
  VOID = 'VOID',
}

export enum ContractorInvoiceBillingCadenceEnum {
  PAY_PERIOD = 'PAY_PERIOD',
  CUSTOM_RANGE = 'CUSTOM_RANGE',
}

export enum ContractorInvoiceDeliveryMethodEnum {
  EMAIL = 'EMAIL',
  DOWNLOAD = 'DOWNLOAD',
  MANUAL = 'MANUAL',
}

export const ContractorAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().optional().nullable(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string().default('US'),
});

export type ContractorAddress = z.infer<typeof ContractorAddressSchema>;

export const ContractorProfileSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid(),
  contractorType: z.nativeEnum(ContractorTypeEnum),
  firstName: z.string(),
  lastName: z.string(),
  billingEmail: z.string().email(),
  billingAddress: ContractorAddressSchema,
  taxIdType: z.string().optional().nullable(),
  taxIdMasked: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  remitToName: z.string().optional().nullable(),
  paymentInstructions: z.string().optional().nullable(),
  website: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ContractorProfile = z.infer<typeof ContractorProfileSchema>;

export const UpsertContractorProfileInputSchema = ContractorProfileSchema.omit({
  id: true,
  organizationId: true,
  memberId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  taxId: z.string().optional(),
});

export type UpsertContractorProfileInput = z.infer<typeof UpsertContractorProfileInputSchema>;

export const ContractorInvoiceTimeEntryReferenceSchema = z.object({
  timeEntryId: z.string().uuid(),
});

export type ContractorInvoiceTimeEntryReference = z.infer<typeof ContractorInvoiceTimeEntryReferenceSchema>;

export const ContractorInvoiceLineSchema = z.object({
  id: z.string().uuid(),
  contractorInvoiceId: z.string().uuid(),
  description: z.string(),
  groupKey: z.string(),
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  moduleName: z.string().nullable().optional(),
  hours: decimalSchema,
  rate: decimalSchema,
  amount: decimalSchema,
  sortOrder: z.number().int(),
  timeEntries: z.array(ContractorInvoiceTimeEntryReferenceSchema).optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ContractorInvoiceLine = z.infer<typeof ContractorInvoiceLineSchema>;

export const ContractorInvoicePayPeriodSchema = z.object({
  id: z.string().uuid(),
  contractorInvoiceId: z.string().uuid(),
  payPeriodId: z.string().uuid(),
  userPayPeriodId: z.string().uuid(),
  memberId: z.string().uuid(),
  includedAt: z.date(),
  payPeriod: z.any().optional(),
  userPayPeriod: z.any().optional(),
});

export type ContractorInvoicePayPeriod = z.infer<typeof ContractorInvoicePayPeriodSchema>;

export const ContractorInvoiceDeliveryRecipientSchema = z.object({
  memberId: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
});

export const ContractorInvoiceExpenseReimbursementItemSchema = z.object({
  expenseId: z.string().uuid(),
  merchant: z.string(),
  amount: z.number(),
  expenseDate: z.string().optional(),
});

export const ContractorInvoiceExpenseReimbursementsSchema = z.object({
  count: z.number().int(),
  totalAmount: z.number(),
  items: z.array(ContractorInvoiceExpenseReimbursementItemSchema).default([]),
});

export type ContractorInvoiceExpenseReimbursements = z.infer<
  typeof ContractorInvoiceExpenseReimbursementsSchema
>;

export const ContractorInvoiceSnapshotSchema = z.object({
  contractorProfile: z.any(),
  organization: z.any(),
  payPeriods: z.array(z.any()).default([]),
  lines: z.array(z.any()).default([]),
  unbilledTime: UnbilledTimeSummarySchema.optional(),
  expenseReimbursements: ContractorInvoiceExpenseReimbursementsSchema.optional(),
  delivery: z.object({
    method: z.nativeEnum(ContractorInvoiceDeliveryMethodEnum),
    sentAt: z.string(),
    recipients: z.array(ContractorInvoiceDeliveryRecipientSchema),
  }).optional(),
}).passthrough();

export type ContractorInvoiceSnapshot = z.infer<typeof ContractorInvoiceSnapshotSchema>;

export const ContractorInvoiceSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid(),
  invoiceNumber: z.string(),
  status: z.nativeEnum(ContractorInvoiceStatusEnum),
  currency: z.string(),
  invoiceStartDate: z.date(),
  invoiceEndDate: z.date(),
  billingCadence: z.nativeEnum(ContractorInvoiceBillingCadenceEnum),
  totalHours: decimalSchema,
  subtotal: decimalSchema,
  totalAmount: decimalSchema,
  notes: z.string().nullable().optional(),
  submittedAt: z.date().nullable().optional(),
  timeApprovedAt: z.date().nullable().optional(),
  payrollApprovedAt: z.date().nullable().optional(),
  paidAt: z.date().nullable().optional(),
  paymentDueDate: z.date().nullable().optional(),
  holdReason: z.string().nullable().optional(),
  heldAt: z.date().nullable().optional(),
  preHoldStatus: z.nativeEnum(ContractorInvoiceStatusEnum).nullable().optional(),
  deliveredAt: z.date().nullable().optional(),
  deliveredMethod: z.nativeEnum(ContractorInvoiceDeliveryMethodEnum).nullable().optional(),
  snapshot: ContractorInvoiceSnapshotSchema.default({
    contractorProfile: {},
    organization: {},
    payPeriods: [],
    lines: [],
  }),
  createdAt: z.date(),
  updatedAt: z.date(),
  payPeriods: z.array(ContractorInvoicePayPeriodSchema).optional(),
  lines: z.array(ContractorInvoiceLineSchema).optional(),
  member: z.any().optional(),
});

export type ContractorInvoice = z.infer<typeof ContractorInvoiceSchema>;

export const PreviewContractorInvoiceInputSchema = z.object({
  userPayPeriodIds: z.array(z.string().uuid()).min(1),
  notes: z.string().optional(),
  billingCadence: z
    .nativeEnum(ContractorInvoiceBillingCadenceEnum)
    .default(ContractorInvoiceBillingCadenceEnum.PAY_PERIOD),
  includeUnbilledTime: z.boolean().default(false),
  includeExpenses: z.boolean().optional(),
});

export type PreviewContractorInvoiceInput = z.infer<typeof PreviewContractorInvoiceInputSchema>;

export const CreateContractorInvoiceInputSchema = PreviewContractorInvoiceInputSchema;

export const MarkContractorInvoicePaidInputSchema = z.object({
  paidAt: z.string().datetime().optional(),
});
export type MarkContractorInvoicePaidInput = z.infer<typeof MarkContractorInvoicePaidInputSchema>;

export const HoldContractorInvoiceInputSchema = z.object({
  holdReason: z.string().min(1).max(500),
});
export type HoldContractorInvoiceInput = z.infer<typeof HoldContractorInvoiceInputSchema>;

export const SetContractorInvoicePaymentDueDateInputSchema = z.object({
  paymentDueDate: z.string().nullable(),
});
export type SetContractorInvoicePaymentDueDateInput = z.infer<typeof SetContractorInvoicePaymentDueDateInputSchema>;

export type CreateContractorInvoiceInput = z.infer<typeof CreateContractorInvoiceInputSchema>;

export const ContractorInvoiceEligiblePayPeriodSchema = z.object({
  payPeriodId: z.string().uuid(),
  userPayPeriodId: z.string().uuid(),
  memberId: z.string().uuid(),
  name: z.string(),
  periodType: z.string(),
  startDate: z.date(),
  endDate: z.date(),
  userPayPeriodStatus: z.string(),
  invoiceEligibilityPolicy: z.nativeEnum(ContractorInvoiceEligibilityPolicyEnum),
  totalHours: decimalSchema,
  approvedHours: decimalSchema,
  pendingHours: decimalSchema,
  canInvoice: z.boolean(),
  blockingReason: z.string().optional().nullable(),
});

export type ContractorInvoiceEligiblePayPeriod = z.infer<typeof ContractorInvoiceEligiblePayPeriodSchema>;
